import { useState, useEffect, useRef, useCallback } from 'react';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import BigNumber from 'bignumber.js';
import {
  StakeOption,
  Process,
  GasStore,
  StakeEncodeStage,
} from '@/types/stake';
import { useCoinInfoContext } from '@/hooks/useCoinInfo';
import { getTokenAddress } from '@/config/address';
import { debounce } from 'lodash-es';
import { useToast } from '@web3/base-hooks';
import { postSyncOrderStatus, postBindOrderTx } from '@/services/order';
import { useChain } from '@/hooks/wallet';
import { ApproveFailType } from '@/types/common';
import { useWallet, useWalletId } from '@/hooks/wallet';
import { useFm } from '@web3/base-hooks';
import { postStakeEncode, postSaveStakeOrder } from '@/services/stake';
import {
  getButtonIsHidden,
  getBtnEmergencyMode,
} from '@/containers/operation/utils';
import { SupportWallets } from '@/types/wallet';
import { PoolButtonType } from '@/types/pool';
import { runAsyncSafe } from '@web3/base-utils';
import { scTrack } from '@web3/base-tracking';
import { Event } from '@/config/track-config';
import { useCurrentChainAddress } from '@/hooks/wallet';
import { providers } from 'ethers';
import { stxAuthenticate, stxSignTrancaction } from '@/utils/stacks';
import { VersionedTransaction } from '@solana/web3.js';
import bs58 from 'bs58';
import { getStakeEstimateToken } from '@/services/stake';
import { PostStakeEncodeParams } from '@/types/stake';
import { useSuiSignTransaction } from '@/containers/operation/transactionHooks/suiTransactionHook';
import { CoinInfo } from '@/types/common';

/**
 * 控制当前staking阶段
 */
export const useStakeStage = () => {
  const [stakeStage, setStakeStage] = useState(StakeEncodeStage.UnKnow);

  const onChangeStakeStage = (v: StakeEncodeStage) => {
    setStakeStage(v);
  };

  return {
    stakeStage,
    onChangeStakeStage,
  };
};

/**
 * gas费相关hook
 */
export const useGasFee = (option: Partial<StakeOption>) => {
  const {
    stakeAmount,
    currentStakeToken,
    process,
    aprPerDay,
    onChangeStakeStage,
  } = option as StakeOption;
  const [gasAmount, setGasAmount] = useState('');
  const [stakeLpInfo, setStakeLpInfo] = useState({} as Partial<CoinInfo>);
  const [loopPercent, setLoopPercent] = useState(0);
  const [gasFeeIsLoading, setGasFeeIsLoading] = useState(false);
  const { poolInfo } = usePoolInfoContext();
  const { coinInfoStore } = useCoinInfoContext();
  const { toastWarn } = useToast();
  const { walletAddress, pubKey } = useCurrentChainAddress(poolInfo.chainCode);
  const address = getTokenAddress(poolInfo.chainCode);
  const gasToken = coinInfoStore?.[`${poolInfo.chainCode}${address}`];
  const gasFeePrice = BigNumber(gasAmount || 0)
    .times(gasToken?.price)
    .toString();
  const timer = useRef<NodeJS.Timeout>();
  const uid = useRef(0);
  const gasStore = {
    hasGasFee: !!gasAmount,
    amount: gasAmount || '0',
    price: gasFeePrice,
    symbol: gasToken?.symbol || '',
    isLoading: gasFeeIsLoading,
    loopPercent: loopPercent * 20,
    isGreatThanProfit: BigNumber(aprPerDay)
      .times(30)
      .lt(gasFeePrice || '0'),
  } as GasStore;
  const currentProcess = useRef(process);
  const fetchGasFee = async (
    selfUid: number,
    selfStakeAmount: string,
    selfContractAddress: string
  ) => {
    setGasFeeIsLoading(true);

    const params: PostStakeEncodeParams = {
      chainId: +poolInfo.chainId,
      poolId: poolInfo.id,
      slippage: '0.5',
      stakeTokens: [
        {
          amount: BigNumber(selfStakeAmount || 0).toString(),
          contractAddress: selfContractAddress,
        },
      ],
      walletAddress,
      pubKey,
    };

    // solana 依赖预估值
    if (poolInfo.chainCode === 'SOL') {
      // 小于最小质押金额时，不请求接口
      const { min } = poolInfo?.poolExtendDTO?.stakeRange || {};
      if (min && BigNumber(selfStakeAmount || 0).lt(min)) {
        return;
      }
      const { result: estimateResult } = await getStakeEstimateToken({
        poolId: +poolInfo.id,
        slippage: '',
        stakeTokens: [
          {
            amount: BigNumber(selfStakeAmount || 0).toString(),
            contractAddress: selfContractAddress,
          },
        ],
      });
      params.slippage = '';
      params.router = estimateResult.router ?? '';
      params.minOutAmount = estimateResult.lpAmount;
      setStakeLpInfo({
        ...estimateResult?.lpCoinDetail,
        amount: estimateResult?.lpAmount,
        router: estimateResult.router ?? '',
        minOutAmount: estimateResult.lpAmount,
      });
    }

    const { success, result, message } = await postStakeEncode(params);
    if (selfUid !== uid.current) return;
    setGasFeeIsLoading(false);

    if (!success) {
      toastWarn(message);
      return false;
    }

    const data = result.txDataList[0];
    setGasAmount(data.gasFee);
    onChangeStakeStage(result.stage);

    return true;
  };

  const startLoopGasFee = useCallback(
    debounce(
      async (
        percent = 0,
        selfStakeAmount: string,
        selfContractAddress: string
      ) => {
        clearTimeout(timer.current);
        if (
          BigNumber(selfStakeAmount).isNaN() ||
          BigNumber(selfStakeAmount).eq(0)
        )
          return;

        setLoopPercent(percent);

        if (percent === 0) {
          uid.current = Math.random();
          const result = await fetchGasFee(
            uid.current,
            selfStakeAmount,
            selfContractAddress
          );
          // 报错则取消轮循
          if (!result) return;
        }
        // 只有在确认弹窗的时候，再轮训gas fee
        if (currentProcess.current !== Process.Confirm) {
          return;
        }
        timer.current = setTimeout(() => {
          if (percent === 4) {
            setLoopPercent(5);
            startLoopGasFee(0, selfStakeAmount, selfContractAddress);
          } else {
            startLoopGasFee(percent + 1, selfStakeAmount, selfContractAddress);
          }
        }, 2000);
      },
      500
    ),
    []
  );

  // 每次更改输入框则清空gasfee
  useEffect(() => {
    setGasAmount('');
  }, [stakeAmount, currentStakeToken.contractAddress, startLoopGasFee]);

  useEffect(() => {
    currentProcess.current = process;
    if (process !== Process.None && process !== Process.Confirm) {
      clearTimeout(timer.current);
      return;
    }

    startLoopGasFee(0, stakeAmount, currentStakeToken.contractAddress);

    return () => {
      clearTimeout(timer.current);
      uid.current = 0;
    };
  }, [
    stakeAmount,
    currentStakeToken.contractAddress,
    startLoopGasFee,
    process,
  ]);

  return {
    gasStore,
    stakeLpInfo,
  };
};

/**
 * 封装了一些代码行数比较多的并且可以复用的请求
 */
export const useFetchStore = (option: Partial<StakeOption>) => {
  const { slippage, stakeAmount, currentStakeToken } = option as StakeOption;
  const { poolInfo } = usePoolInfoContext();
  const { id } = poolInfo;
  const fm = useFm();
  const { walletAddress, pubKey } = useCurrentChainAddress(poolInfo.chainCode);
  const { toastWarn } = useToast();

  // 生成staking订单
  const createOrder = async (stage: StakeEncodeStage) => {
    const { success, result } = await postSaveStakeOrder({
      chainId: +poolInfo.chainId,
      poolId: id,
      slippage,
      stage,
      stakeTokens: [
        {
          amount: BigNumber(stakeAmount || 0).toString(),
          contractAddress: currentStakeToken.contractAddress,
        },
      ],
      walletAddress,
    });

    if (!success) {
      toastWarn(fm('mpsToastUpcoming'));
    }

    return {
      success,
      result,
    };
  };

  // 同步给订单错误态
  const updateFailedOrderStatus = async (
    isCancel: boolean,
    orderCode: string,
    orderTxId: number
  ) => {
    await postSyncOrderStatus({
      chainId: +poolInfo.chainId,
      orderCode,
      orderTxId,
      status: isCancel ? ApproveFailType.CANCEL : ApproveFailType.UN_KNOWN,
      walletAddress,
    });
  };

  // 同步给订单成功态
  const updateSuccessOrderStatus = async (
    hash: string,
    orderCode: string,
    orderTxId: number
  ) => {
    await postBindOrderTx({
      chainId: +poolInfo.chainId,
      orderCode,
      orderTxId,
      txHash: hash,
      walletAddress,
    });
  };

  // 获取编码
  const getEncodeData = async (minOutAmount?: string, router?: string) => {
    let success = true;
    const params: PostStakeEncodeParams = {
      chainId: +poolInfo.chainId,
      poolId: id,
      slippage,
      stakeTokens: [
        {
          amount: BigNumber(stakeAmount || 0).toString(),
          contractAddress: currentStakeToken.contractAddress,
        },
      ],
      walletAddress,
      pubKey,
    };

    if (minOutAmount) {
      params.minOutAmount = minOutAmount;
    }
    if (router) {
      params.router = router;
    }

    const {
      success: encodeSuccess,
      result,
      message,
    } = await postStakeEncode(params);

    if (!encodeSuccess) {
      toastWarn(message);
      success = false;
    }

    if (!result.txDataList[0]) {
      toastWarn(fm('systemBusyTryLater'));
      success = false;
    }

    return {
      success,
      result,
    };
  };

  return {
    createOrder,
    updateFailedOrderStatus,
    updateSuccessOrderStatus,
    getEncodeData,
  };
};

export const useStake = (option: Partial<StakeOption>) => {
  const [confirmButtonIsLoading, setConfirmButtonIsLoading] = useState(false);
  const {
    process,
    onChangeStakeStage,
    onChangeProcess,
    slippage = '',
    stakeAmount = '',
    currentStakeToken,
    stakeLpInfo,
  } = option as StakeOption;
  const { poolInfo } = usePoolInfoContext();
  const { switchToPoolChain } = useChain();
  const [orderCode, setOrderCode] = useState('');
  const { toastWarn, toastSuccess } = useToast();
  const { evmThirdPartyProvider, solanaThirdPartyProvider } = useWallet();
  const { walletAddress } = useCurrentChainAddress(poolInfo.chainCode);
  const fm = useFm();
  const {
    createOrder,
    updateFailedOrderStatus,
    updateSuccessOrderStatus,
    getEncodeData,
  } = useFetchStore(option);
  const walletId = useWalletId();
  const { currentWalletType } = useWallet();
  const { suiSignAndTransaction } = useSuiSignTransaction();
  // evm质押
  const evmStake = async () => {
    // 切链
    const switchChainIsSuccess = await switchToPoolChain(poolInfo.chainId);
    if (!switchChainIsSuccess) return;

    // 获取编码
    const { success: encodeSuccess, result } = await getEncodeData();

    if (!encodeSuccess) return;

    // 创建订单
    const {
      success: saveOrderSuccess,
      result: { orderCode, orderTxId },
    } = await createOrder(result.stage);

    if (!saveOrderSuccess) return;

    let txHash = '';

    try {
      // 签名
      const txData = result.txDataList[0];
      const provider = new providers.Web3Provider(evmThirdPartyProvider);
      const signer = provider.getSigner();
      txHash = (
        await signer.sendTransaction({
          from: txData.from,
          to: txData.to,
          data: txData.data,
          value: txData.value,
        })
      ).hash;
    } catch (e: any) {
      // 是否为手动拒绝
      const isCancel = e?.code?.includes?.('ACTION_REJECTED');
      if (!isCancel) {
        toastWarn(fm('systemBusyTryLater'));
      }
      updateFailedOrderStatus(isCancel, orderCode, orderTxId);

      return;
    }

    // 同步订单成功态
    updateSuccessOrderStatus(txHash, orderCode, orderTxId);

    // 结束
    if (result.stage === StakeEncodeStage.ApproveTrade) {
      onChangeProcess(Process.Result);
      setOrderCode(orderCode);
    }

    if (result.stage === StakeEncodeStage.ApproveAmount) {
      toastSuccess(fm('success'));
      onChangeStakeStage(StakeEncodeStage.ApproveTrade);
    }
  };

  // stx质押
  const stacksStake = async () => {
    // 授权
    const { success: authSuccess } = await stxAuthenticate(walletAddress);

    if (!authSuccess) return;

    // 获取编码
    const { success: encodeSuccess, result } = await getEncodeData();

    if (!encodeSuccess) return;

    // 创建订单
    const {
      success: saveOrderSuccess,
      result: { orderCode, orderTxId },
    } = await createOrder(result.stage);

    if (!saveOrderSuccess) return;

    const { success: signSuccess, hash: txHash } = await stxSignTrancaction(
      result.txDataList[0].data
    );

    if (!signSuccess) {
      updateFailedOrderStatus(true, orderCode, orderTxId);
      return;
    }

    // 同步订单成功态
    updateSuccessOrderStatus(txHash, orderCode, orderTxId);

    // 结束
    if (result.stage === StakeEncodeStage.ApproveTrade) {
      onChangeProcess(Process.Result);
      setOrderCode(orderCode);
    }

    if (result.stage === StakeEncodeStage.ApproveAmount) {
      toastSuccess(fm('success'));
      onChangeStakeStage(StakeEncodeStage.ApproveTrade);
    }
  };

  const solStake = async () => {
    // 获取编码
    const { success: encodeSuccess, result } = await getEncodeData(
      stakeLpInfo?.minOutAmount,
      stakeLpInfo?.router ?? ''
    );
    if (!encodeSuccess || !result?.txDataList?.[0]?.data) {
      toastWarn(fm('systemBusyTryLater'));
      return;
    }

    const provider = solanaThirdPartyProvider;

    const transaction = VersionedTransaction.deserialize(
      bs58.decode(result.txDataList[0].data)
    );
    // 钱包签名
    const [errTx, tx] = await runAsyncSafe<
      { signatures: Array<Uint8Array>; serialize: () => Uint8Array },
      Error
    >(provider.signTransaction(transaction));

    if (errTx) {
      const isReject = errTx.message.indexOf('reject') > -1;
      toastWarn(
        isReject ? fm('walletRejected') : fm('submitTransactionFailed')
      );
      return;
    }
    const txDataList = result.txDataList[0];

    // 创建订单让后端广播
    const { success, result: OrderResult } = await postSaveStakeOrder({
      chainId: +poolInfo.chainId,
      poolId: poolInfo.id,
      slippage,
      stage: result.stage,
      stakeTokens: [
        {
          amount: BigNumber(stakeAmount || 0).toString(),
          contractAddress: currentStakeToken.contractAddress,
        },
      ],
      walletAddress,
      orderCode: result.orderCode,
      broadcast: 1,
      broadcastData: [
        {
          signData: bs58.encode(tx.serialize()),
          chainId: +poolInfo.chainId,
          from: txDataList?.from ?? '',
          to: txDataList?.to ?? '',
          value: txDataList?.value ?? '',
          txHash: bs58.encode(tx.signatures[0]),
        },
      ],
      walletId: walletId?.walletId,
      walletType: currentWalletType,
      useThirdConfig: false,
    });

    if (!success) {
      toastWarn(fm('systemBusyTryLater'));
      return;
    }
    // 结束
    if (success) {
      onChangeProcess(Process.Result);
      setOrderCode(OrderResult.orderCode);
    }
  };

  // sui质押
  const suiStake = async () => {
    // 获取编码
    const { success: encodeSuccess, result } = await getEncodeData();

    if (!encodeSuccess) {
      toastWarn(fm('systemBusyTryLater'));
      return;
    }
    // 创建订单
    const {
      success: saveOrderSuccess,
      result: { orderCode, orderTxId },
    } = await createOrder(result.stage);

    if (!saveOrderSuccess) return;

    // 钱包签名
    const [errTx, transactionResult] = await suiSignAndTransaction(
      result.txDataList[0].data
    );

    if (errTx) {
      const isReject = errTx.message.indexOf('reject') > -1;
      toastWarn(
        isReject ? fm('walletRejected') : fm('submitTransactionFailed')
      );
      // 同步订失败状态
      updateFailedOrderStatus(isReject, orderCode, orderTxId);
      return;
    }
    // 同步订单成功状态
    updateSuccessOrderStatus(transactionResult.digest, orderCode, orderTxId);

    // // 结束
    if (result.stage === StakeEncodeStage.ApproveTrade) {
      onChangeProcess(Process.Result);
      setOrderCode(orderCode);
    }

    if (result.stage === StakeEncodeStage.ApproveAmount) {
      toastSuccess(fm('success'));
      onChangeStakeStage(StakeEncodeStage.ApproveTrade);
    }
  };

  useEffect(() => {
    if (process === Process.None) {
      setConfirmButtonIsLoading(false);
    }
  }, [process]);

  // 开始质押
  const beginStake = async () => {
    setConfirmButtonIsLoading(true);

    if (poolInfo.chainCode === 'STX') {
      await stacksStake();
    } else if (poolInfo.chainCode === 'SOL') {
      await solStake();
    } else if (poolInfo.chainCode === 'SUI') {
      await suiStake();
    } else {
      await evmStake();
    }

    setConfirmButtonIsLoading(false);
  };

  return {
    orderCode,
    confirmButtonIsLoading,
    beginStake,
  };
};

export const useButtonStatus = (option: Partial<StakeOption>) => {
  const {
    stakeAmount,
    stakeStage,
    confirmButtonIsLoading,
    gasStore,
    onChangeProcess,
  } = option as StakeOption;
  const fm = useFm();
  const {
    poolInfo: { supportWallets },
  } = usePoolInfoContext();
  const { poolInfo } = usePoolInfoContext();
  const { walletAddress } = useCurrentChainAddress(poolInfo.chainCode);
  const stakeIsHidden = getButtonIsHidden(
    PoolButtonType.Stake,
    poolInfo.poolExtendDTO
  );

  const stakeEmergencyDisable = getBtnEmergencyMode(
    PoolButtonType.Stake,
    poolInfo.poolExtendDTO,
    walletAddress
  );
  const [textError, setTextError] = useState('');

  const onChangeTextError = (v: string) => {
    setTextError(v);
  };

  const buttonText = (() => {
    if (
      supportWallets.length &&
      !supportWallets.includes(SupportWallets.thirdWallet)
    ) {
      return fm('switchWallet');
    }
    if (BigNumber(stakeAmount).isNaN() || BigNumber(stakeAmount).eq(0)) {
      return fm('stakeNow1');
    }
    if (
      stakeStage === StakeEncodeStage.ApproveAmount &&
      !confirmButtonIsLoading
    ) {
      return fm('btn_approve');
    }
    if (
      stakeStage === StakeEncodeStage.ApproveAmount &&
      confirmButtonIsLoading
    ) {
      return fm('btn_approveing');
    }
    return fm('stakeNow1');
  })();

  const buttonDisabled = (() => {
    if (
      supportWallets.length &&
      !supportWallets.includes(SupportWallets.thirdWallet)
    ) {
      return true;
    }

    if (BigNumber(stakeAmount || 0).lte(0)) {
      return true;
    }

    if (!gasStore.hasGasFee) {
      return true;
    }

    if (textError?.length) {
      return true;
    }
  })();

  const onClickStake = () => {
    scTrack(Event.Web3UserClick, {
      page_name: 'StakingWeb_bbSOLPagestake_start',
      button_name: 'Stake',
    });

    if (gasStore.isGreatThanProfit) {
      onChangeProcess(Process.GasFeeTip);
      return;
    }

    onChangeProcess(Process.Confirm);
  };

  return {
    stakeEmergencyDisable,
    buttonText,
    stakeIsHidden,
    buttonDisabled,
    textError,
    onClickStake,
    onChangeTextError,
  };
};
